import {
  signIn,
  signOut,
  signUp as amplifySignUp,
  confirmSignUp,
  resendSignUpCode,
  getCurrentUser,
  fetchUserAttributes,
  fetchAuthSession,
  resetPassword,
  confirmResetPassword,
  type ResetPasswordOutput,
  type ConfirmResetPasswordInput,
} from 'aws-amplify/auth';

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface UserInfo {
  username: string;
  email: string;
  name?: string;
  groups: string[];
}

export interface SignUpInput {
  nome: string;
  cpf: string;
  email: string;
  password: string;
}

export async function login(credentials: LoginCredentials): Promise<UserInfo> {
  await signIn({ username: credentials.email, password: credentials.password });
  return getCurrentUserInfo();
}

export async function logout(): Promise<void> {
  await signOut();
}

export async function signUpRandonneur(input: SignUpInput): Promise<void> {
  await amplifySignUp({
    username: input.email,
    password: input.password,
    options: {
      userAttributes: {
        email: input.email,
        name: input.nome,
        'custom:cpf': input.cpf,
      },
    },
  });
}

export async function confirmEmail(email: string, code: string): Promise<void> {
  await confirmSignUp({ username: email, confirmationCode: code });
}

export async function resendConfirmationCode(email: string): Promise<void> {
  await resendSignUpCode({ username: email });
}

export async function getCurrentUserInfo(): Promise<UserInfo> {
  const [user, attributes, session] = await Promise.all([
    getCurrentUser(),
    fetchUserAttributes(),
    fetchAuthSession(),
  ]);

  const groups = (session.tokens?.accessToken?.payload['cognito:groups'] as string[]) ?? [];

  return {
    username: user.username,
    email: attributes.email ?? user.username,
    name: attributes.name,
    groups,
  };
}

export async function isAuthenticated(): Promise<boolean> {
  try {
    await getCurrentUser();
    return true;
  } catch {
    return false;
  }
}

export async function getAccessToken(): Promise<string | null> {
  try {
    const session = await fetchAuthSession();
    return session.tokens?.accessToken?.toString() ?? null;
  } catch {
    return null;
  }
}

export async function initiatePasswordReset(email: string): Promise<ResetPasswordOutput> {
  return resetPassword({ username: email });
}

export async function confirmPasswordResetFlow(params: ConfirmResetPasswordInput): Promise<void> {
  await confirmResetPassword(params);
}
